/**
 * 门店实时数据 API 服务
 *
 * 请求方式：
 * 1. 整店数据：GET /api/store-revenue/data?hotelId=xxx
 *    - 页面加载时先加载 5 个门店（每个门店一次请求整店数据），滑动到底部懒加载更多，刷新某门店再次请求整店数据
 * 2. 单组件数据：GET /api/store-revenue/component?hotelId=xxx&componentType=xxx
 *    - 按需拉取单个组件（如 room-status、order-traffic）时使用
 */

import axios from 'axios';

/** 门店营收后端 API 的请求头：Token 全部由后端 getStableToken() 处理，前端不传；禁用缓存避免 304 返回旧空数据 */
function storeRevenueHeaders(): Record<string, string> {
  const win =
    typeof globalThis !== 'undefined' &&
    'window' in globalThis
      ? (globalThis.window as Window)
      : undefined;
  const isScreenshotMode =
    !!win &&
    new URLSearchParams(win.location.search).get('screenshot') === '1';
  if (isScreenshotMode) {
    return {
      'Content-Type': 'application/json',
    };
  }
  return {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
  };
}

/**
 * 已废弃：Token 全部由后端处理，前端不再获取或传递 token。
 * 保留此方法仅为兼容旧调用，直接返回 null，调用方应改为走后端接口。
 */
export async function ensureStoreRevenueToken(): Promise<string | null> {
  return null;
}

/**
 * 门店实时数据查询参数
 * 只传：酒店 ID + 起始时间（可选，不传则后端按当前时刻）。结束时间由后端按需拼接（如 7 天、30 天等）。
 */
export interface StoreRevenueQueryParams {
  hotelId: number | string;
  startDate?: number; // Unix 时间戳（秒），不传则后端默认为当前时刻
}

/**
 * 房态信息数据
 */
/** 近期房型房价单行：24h + 7d 两组挂牌价（绝对价格） */
export interface HourlyPriceByRoomRow {
  id: string;
  label: string;
  priceData24h: number[];
  priceData7d: number[];
  currentPrice?: number;
}

export interface HourlyPriceByRoomData {
  dataSource: HourlyPriceByRoomRow[];
}

/** 房态健康图表块（与后端 RoomHealthData 一致） */
export interface RoomHealthChartBlock {
  chartData: {
    categories: string[];
    dataset: Record<string, number[]>;
    axes: Record<string, unknown>;
    series: Array<Record<string, unknown>>;
  };
}

export interface RoomHealthBundleData {
  last7Days: RoomHealthChartBlock;
  last30Days: RoomHealthChartBlock;
  last12Months: RoomHealthChartBlock;
}

export interface OccupancyAnalysisChartBlock {
  chartData: {
    categories: string[];
    dataset: Record<string, number[]>;
    axes: Record<string, unknown>;
    series: Array<Record<string, unknown>>;
  };
}

export interface OccupancyAnalysisBundleData {
  last7Days: OccupancyAnalysisChartBlock;
  last30Days: OccupancyAnalysisChartBlock;
  last12Months: OccupancyAnalysisChartBlock;
}

export interface RoomStatusData {
  dataSource: Array<{
    id: string; // 房型ID，格式：room-{index} 或 room-{roomId}
    label: string; // 房型名称，如 "豪华商务大床房"
    progress: number; // 出租率（0-100），计算方式：(roomCount - quantityOpen) / roomCount * 100
    quantity: number; // 总房间数（roomCount），用于控制进度条背景宽度（灰条）
    priceData: number[]; // 价格趋势数据（12个节点，每小时1个），归一化，用于折线图显示
    normalizedReferenceLines?: Array<{
      value: number; // 归一化后的 Y 轴值
      color?: string; // 辅助线颜色，如 "#ff4d4f"（红色）或 "#1890ff"（蓝色）
      strokeDasharray?: string; // 虚线样式，如 "5 5"
      label?: string; // 辅助线标签，如 "年同比" 或 "周环比"
    }>; // 归一化后的辅助线配置，用于价格折线图显示对比数据
  }>;
}

/**
 * 订单流速数据
 */
// 单个图表数据结构（用于小时或天）
interface OrderTrafficChartData {
  categories: string[];
  dataset: {
    channel1Orders: number[];
    channel2Orders: number[];
    channel3Orders: number[];
    channel4Orders: number[];
    averagePrice: number[];
  };
  axes: {
    left: {
      id: string;
      position: 'left';
      name: string;
      unit: string;
      max: number;
    };
    right: {
      id: string;
      position: 'right';
      name: string;
      unit: string;
      min: number;
      max: number;
    };
  };
  series: Array<{
    id: string;
    name: string;
    dataKey: string;
    type: 'bar' | 'line';
    axisId: string;
    stackId?: string;
    color?: string;
  }>;
  comparison: {
    yearOverYear: number;
    weekOverWeek: number;
  };
}

export interface OrderTrafficData {
  // 小时粒度数据（24小时）
  hourly?: OrderTrafficChartData;
  // 天粒度数据（7天）
  daily?: OrderTrafficChartData;
  // 原始数据（用于前端自定义处理）
  rawData?: {
    channelNames?: string[];
    orders?: any[];
    baseTimestamp?: number;
  };
  // 向后兼容：默认使用小时数据
  chartData?: OrderTrafficChartData;
}

/**
 * 远期房态数据
 */
export interface RoomStatusRecentlyData {
  baseChartData: {
    categories: string[];
    dataset: {
      [roomTypeName: string]: number[] | number[][];
      klineData: number[][];
      averagePrice: number[];
      highestPrice: number[];
      lowestPrice: number[];
      yearOverYear: number[];
      weekOverWeek: number[];
    };
    axes: {
      left: {
        id: string;
        position: 'left';
        name: string;
        unit: string;
        min: number;
        max: number;
      };
      right: {
        id: string;
        position: 'right';
        name: string;
        unit: string;
        min: number;
        max: number;
      };
    };
    baseSeries: Array<{
      id: string;
      name: string;
      dataKey: string;
      type: 'bar';
      axisId: string;
      stackId: string;
    }>;
    comparison: {
      yearOverYear: {
        value: number[];
        color: string;
        strokeDasharray: string;
        label: string;
      };
      weekOverWeek: {
        value: number[];
        color: string;
        strokeDasharray: string;
        label: string;
      };
    };
  };
}

/**
 * 价格倒挂数据
 */
export interface PriceAlertData {
  chartData: {
    categories: string[];
    dataset: {
      klineData: number[][];
      averagePrice: number[];
      highestPrice: number[];
      lowestPrice: number[];
      yearOverYear: number[];
      weekOverWeek: number[];
    };
    axes: {
      left: {
        id: string;
        position: 'left';
        name: string;
        unit: string;
        min: number;
        max: number;
      };
    };
    series: Array<{
      id: string;
      name: string;
      dataKey: string;
      type: 'candlestick' | 'line';
      axisId: string;
    }>;
  };
}

/** PMS 业务报告单条（与后端 RevparBusinessReport 一致） */
export interface RevparBusinessReportItem {
  id: number;
  hotelId: number;
  hotelName?: string;
  hotelCode?: string;
  date: number; // Unix 时间戳（秒）
  roomTotal?: number;
  occupancyRate?: number;
  adr?: number; // 平均房价
  roomRevpar?: number; // 房间 RevPAR
  allRevpar?: number;
  roomRevenue?: number;
  allRevenue?: number;
  [key: string]: unknown;
}

/** PMS businessReportList 响应 */
export interface BusinessReportListResponse {
  code: number;
  msg?: string;
  message?: string;
  data?: {
    total: number;
    list: RevparBusinessReportItem[];
    pageNum?: number;
    pageSize?: number;
    pages?: number;
  };
}

/**
 * 前端直调 PMS 业务报告列表（用于 RevPAR 等）
 * GET {BASE}/qiuguo-pms/pms/mainReport/businessReportList?hotelId=xxx&dateType=0|1&startDate=YYYY-MM-DD&endDate=YYYY-MM-DD
 */
/**
 * 业务报告：走后端代理（后端统一调 PMS 并写 Redis）
 * GET /api/store-revenue/proxy/business-report?hotelId=xxx&dateType=0&startDate=&endDate=
 */
export async function getBusinessReportListFromThirdParty(
  hotelId: number | string,
  params: { dateType: number; startDate: string; endDate: string; pageNum?: number; pageSize?: number }
): Promise<RevparBusinessReportItem[]> {
  const response = await axios.get<{ code: number; data?: { list?: RevparBusinessReportItem[] }; message?: string }>(
    '/api/store-revenue/proxy/business-report',
    {
      params: {
        hotelId: String(hotelId),
        dateType: params.dateType,
        startDate: params.startDate,
        endDate: params.endDate,
        pageNum: params.pageNum ?? 1,
        pageSize: params.pageSize ?? 500,
      },
      timeout: 30000,
    }
  );
  if (response.data?.code !== 1) {
    throw new Error(response.data?.message ?? '业务报告接口异常');
  }
  if (!response.data?.data?.list) return [];
  return response.data.data.list;
}

/**
 * RevPAR 数据
 */
export interface RevPARData {
  chartData: {
    categories: string[];
    dataset: {
      priceKlineData: number[][];
      RevPAR: number[];
      RevPARLastYear: number[];
    };
    axes: {
      left: {
        id: string;
        position: 'left';
        name: string;
        unit: string;
        min: number;
        max: number;
        axisLabel?: { formatter: (value: number) => string };
      };
    };
    series: Array<{
      id: string;
      name: string;
      dataKey: string;
      type: 'candlestick' | 'line' | 'bar';
      axisId: string;
      color?: string;
      lineStyle?: { type?: string };
    }>;
  };
}

/**
 * 渠道信息数据
 */
export interface ChannelInfoData {
  chartData: {
    categories: string[];
    dataset: {
      volume: number[];
      customerUnitPrice: number[];
    };
    axes: {
      left: {
        id: string;
        position: 'left';
        name: string;
        unit: string;
        min: number;
        max: number;
      };
      right: {
        id: string;
        position: 'right';
        name: string;
        unit: string;
        min: number;
        max: number;
      };
    };
    series: Array<{
      id: string;
      name: string;
      dataKey: string;
      type: 'bar' | 'line';
      axisId: string;
      smooth?: boolean;
    }>;
  };
}

/**
 * 渠道价格数据
 */
export interface ChannelPriceData {
  chartData: any; // 渠道价格图表数据（原始格式，需要转换）
}

/**
 * 门店完整实时数据响应
 */
export interface StoreRevenueDataResponse {
  code: number;
  message?: string;
  data: {
    roomStatus: RoomStatusData;
    orderTraffic: OrderTrafficData;
    roomStatusRecently: RoomStatusRecentlyData;
    priceAlert: PriceAlertData;
    revPARChart: RevPARData;
    channelInfoChart: ChannelInfoData;
    channelPrice: ChannelPriceData;
    roomHealth?: any; // 房态健康度
    customerSource?: any; // 客源结构分析
    discountAnalysis?: any; // 折扣率分析
    channelAnalysis?: any; // 渠道分析
    occupancyAnalysis?: any; // 出租率分析
    operationEfficiency?: any; // 运营效率分析
  };
}

/**
 * 时间方向：past=过去 7天/30天/12月，future=未来 7天/30天/12月
 */
export type TimeDirection = 'past' | 'future';

/**
 * 组件四时间段数据（current / 7天 / 30天 / 12月）
 * 与门店营收页 ComponentDataPackage 一致；timeDirection 表示 7d/30d/12m 是过去还是未来
 */
export interface ComponentDataByTimeRanges {
  timeDirection?: TimeDirection;
  current: any;
  last7Days?: any;
  last30Days?: any;
  last12Months?: any;
}

/**
 * 按组件名获取该组件的四时间段数据
 * 后端约定：传 hotelId + componentType + timeDirection，返回 { timeDirection, current, last7Days, last30Days, last12Months }
 * 接口：GET /api/store-revenue/component-data?hotelId=xxx&componentType=xxx&timeDirection=past|future
 */
export async function getComponentDataByTimeRanges(
  hotelId: number | string,
  componentType: string,
  timeDirection: TimeDirection = 'past'
): Promise<ComponentDataByTimeRanges> {
  const url = '/api/store-revenue/component-data';

  try {
    const response = await axios.get<{
      code: number;
      message?: string;
      data: ComponentDataByTimeRanges;
    }>(url, {
      headers: storeRevenueHeaders(),
      params: { hotelId, componentType, timeDirection },
    });

    if (response.data?.code === 1 && response.data?.data != null) {
      return response.data.data;
    }
    throw new Error(response.data?.message ?? '获取组件四时间段数据失败');
  } catch (error: any) {
    if (error.response?.status === 503) {
      throw new Error(error.response?.data?.message ?? '门店营收服务未配置 Token，请联系管理员');
    }
    if (error.response?.status === 401) {
      throw new Error('Token 无效或已过期，请重新登录');
    }
    if (error.response?.status === 500) {
      throw new Error(
        error.response?.data?.message ?? '组件数据服务暂时不可用，将使用本地数据'
      );
    }
    throw error;
  }
}

/** FSM 实时房态统计列表单条（GET /qiuguo-fsm/fsm/hotel/aliasCountList） */
export interface FsmAliasCountItem {
  id: number;
  name: string;
  totalRoomCount: number;
  roomAvaliableCount: number;
  roomStopCount: number;
  roomNightCount: number;
  validTakeCount: number;
  validHourTakeCount: number;
  allTakeCount: number;
  hourPrice: number;
  dayPrice: number;
  hotelTypeSort: number;
}

/** FSM aliasCountList 响应 */
// 统一 baseUrl：所有前端直连第三方均用此方法，见 config/apiBaseUrl.ts
export { getFsmBaseUrl } from '../config/apiBaseUrl';

/**
 * 房态数据：优先走后端代理（后端统一调第三方并写 Redis），与 localStorage 分层缓存一致
 * GET /api/store-revenue/proxy/room-status?hotelId=xxx
 */
export async function getRoomStatusFromThirdParty(hotelId: number | string): Promise<FsmAliasCountItem[]> {
  const response = await axios.get<{ code: number; data?: FsmAliasCountItem[] }>(
    '/api/store-revenue/proxy/room-status',
    { params: { hotelId }, timeout: 20000 }
  );
  if (response.data?.code === 1 && Array.isArray(response.data?.data)) return response.data.data;
  return [];
}

/**
 * 近期房型房价：过去 24 小时 + 过去 7 天两组挂牌价
 * GET /api/store-revenue/proxy/hourly-price-by-room?hotelId=xxx
 */
export async function getHourlyPriceByRoomFromProxy(
  hotelId: number | string
): Promise<HourlyPriceByRoomData> {
  const response = await axios.get<{ code: number; data?: HourlyPriceByRoomData }>(
    '/api/store-revenue/proxy/hourly-price-by-room',
    { params: { hotelId }, timeout: 30000 }
  );
  if (response.data?.code === 1 && response.data?.data) return response.data.data;
  return { dataSource: [] };
}

/** 房态健康：过去 7 天 / 30 天 / 12 个月三组 */
export async function getRoomHealthFromProxy(
  hotelId: number | string
): Promise<RoomHealthBundleData> {
  const response = await axios.get<{ code: number; data?: RoomHealthBundleData }>(
    '/api/store-revenue/proxy/room-health',
    { params: { hotelId }, timeout: 60000 }
  );
  if (response.data?.code === 1 && response.data?.data) return response.data.data;
  const empty: RoomHealthChartBlock = {
    chartData: { categories: [], dataset: {}, axes: { left: { id: 'left', position: 'left', name: '房间数', unit: '间', min: 0, max: 100 } }, series: [] },
  };
  return { last7Days: empty, last30Days: empty, last12Months: empty };
}

export interface OrderTrafficChartBlock {
  categories: string[];
  dataset: Record<string, number[]>;
  axes: Record<string, unknown>;
  series: Array<Record<string, unknown>>;
  comparison?: { yearOverYear?: number; weekOverWeek?: number };
}

export interface OrderTrafficBundleData {
  last24Hours: OrderTrafficChartBlock;
  last7Days: OrderTrafficChartBlock;
}

export interface ChannelInfoChartBlock {
  categories: string[];
  dataset: { volume: number[]; customerUnitPrice: number[] };
  axes: Record<string, unknown>;
  series: Array<Record<string, unknown>>;
}

export interface ChannelInfoBundleData {
  last7Days: { chartData: ChannelInfoChartBlock };
  last30Days: { chartData: ChannelInfoChartBlock };
  last12Months: { chartData: ChannelInfoChartBlock };
}

export async function getChannelInfoFromProxy(
  hotelId: number | string
): Promise<ChannelInfoBundleData> {
  const response = await axios.get<{ code: number; data?: ChannelInfoBundleData }>(
    '/api/store-revenue/proxy/channel-info',
    { params: { hotelId }, timeout: 60000 }
  );
  if (response.data?.code === 1 && response.data?.data) return response.data.data;
  const empty: ChannelInfoChartBlock = {
    categories: [],
    dataset: { volume: [], customerUnitPrice: [] },
    axes: {
      left: { id: 'left', position: 'left', name: '订单量', unit: '单', min: 0, max: 220 },
      right: { id: 'right', position: 'right', name: '客单价', unit: '元', min: 0, max: 3100 },
    },
    series: [],
  };
  const block = { chartData: empty };
  return { last7Days: block, last30Days: block, last12Months: block };
}

export interface RevparBundleData {
  last7Days: RevPARData;
  last30Days: RevPARData;
  last12Months: RevPARData;
}

export async function getRevparFromProxy(
  hotelId: number | string
): Promise<RevparBundleData> {
  const response = await axios.get<{ code: number; data?: RevparBundleData }>(
    '/api/store-revenue/proxy/revpar',
    { params: { hotelId }, timeout: 60000 }
  );
  if (response.data?.code === 1 && response.data?.data) return response.data.data;
  const emptyChart: RevPARData['chartData'] = {
    categories: [],
    dataset: { priceKlineData: [], RevPAR: [], RevPARLastYear: [] },
    axes: {
      left: { id: 'left', position: 'left', name: '可售房收入', unit: '元', min: 0, max: 100 },
    },
    series: [],
  };
  const block: RevPARData = { chartData: emptyChart };
  return { last7Days: block, last30Days: block, last12Months: block };
}

export interface ChannelAnalysisChartBlock {
  categories: string[];
  dataset: { roomNights: number[]; rates: number[]; revenue: number[] };
  axes: Record<string, unknown>;
  series: Array<Record<string, unknown>>;
}

export interface ChannelAnalysisBundleData {
  last7Days: { chartData: ChannelAnalysisChartBlock };
  last30Days: { chartData: ChannelAnalysisChartBlock };
  last12Months: { chartData: ChannelAnalysisChartBlock };
}

export async function getChannelAnalysisFromProxy(
  hotelId: number | string
): Promise<ChannelAnalysisBundleData> {
  const response = await axios.get<{ code: number; data?: ChannelAnalysisBundleData }>(
    '/api/store-revenue/proxy/channel-analysis',
    { params: { hotelId }, timeout: 60000 }
  );
  if (response.data?.code === 1 && response.data?.data) return response.data.data;
  const empty: ChannelAnalysisChartBlock = {
    categories: [],
    dataset: { roomNights: [], rates: [], revenue: [] },
    axes: {
      left: { id: 'left', position: 'left', name: '间夜数', unit: '间', min: 0, max: 100 },
      right: { id: 'right', position: 'right', name: '收入', unit: '元', min: 0, max: 10000 },
    },
    series: [],
  };
  const block = { chartData: empty };
  return { last7Days: block, last30Days: block, last12Months: block };
}

export interface OperationEfficiencyChartBlock {
  categories: string[];
  dataset: {
    earlyCheckout: number[];
    noshow: number[];
    orderTake: number[];
    ratio: number[];
  };
  axes: Record<string, unknown>;
  series: Array<Record<string, unknown>>;
}

export interface OperationEfficiencyBundleData {
  last7Days: { chartData: OperationEfficiencyChartBlock };
  last30Days: { chartData: OperationEfficiencyChartBlock };
  last12Months: { chartData: OperationEfficiencyChartBlock };
  next7Days: { chartData: OperationEfficiencyChartBlock };
  next30Days: { chartData: OperationEfficiencyChartBlock };
}

export async function getOperationEfficiencyFromProxy(
  hotelId: number | string
): Promise<OperationEfficiencyBundleData> {
  const response = await axios.get<{ code: number; data?: OperationEfficiencyBundleData }>(
    '/api/store-revenue/proxy/operation-efficiency',
    { params: { hotelId }, timeout: 60000 }
  );
  if (response.data?.code === 1 && response.data?.data) return response.data.data;
  const empty: OperationEfficiencyChartBlock = {
    categories: [],
    dataset: { earlyCheckout: [], noshow: [], orderTake: [], ratio: [] },
    axes: {
      left: { id: 'left', position: 'left', name: '数量', unit: '间', min: 0, max: 100 },
      right: { id: 'right', position: 'right', name: '非正常占比', unit: '%', min: 0, max: 100 },
    },
    series: [],
  };
  const block = { chartData: empty };
  return {
    last7Days: block,
    last30Days: block,
    last12Months: block,
    next7Days: block,
    next30Days: block,
  };
}

export interface CustomerSourceChartBlock {
  categories: string[];
  dataset: {
    fit: number[];
    team: number[];
    member: number[];
    unit: number[];
    ota: number[];
    intro: number[];
    occupancyRate: number[];
  };
  axes: Record<string, unknown>;
  series: Array<Record<string, unknown>>;
}

export interface CustomerSourceBundleData {
  last7Days: { chartData: CustomerSourceChartBlock };
  last30Days: { chartData: CustomerSourceChartBlock };
  last12Months: { chartData: CustomerSourceChartBlock };
  next7Days: { chartData: CustomerSourceChartBlock };
  next30Days: { chartData: CustomerSourceChartBlock };
}

export async function getCustomerSourceFromProxy(
  hotelId: number | string
): Promise<CustomerSourceBundleData> {
  const response = await axios.get<{ code: number; data?: CustomerSourceBundleData }>(
    '/api/store-revenue/proxy/customer-source',
    { params: { hotelId }, timeout: 60000 }
  );
  if (response.data?.code === 1 && response.data?.data) return response.data.data;
  const empty: CustomerSourceChartBlock = {
    categories: [],
    dataset: { fit: [], team: [], member: [], unit: [], ota: [], intro: [], occupancyRate: [] },
    axes: {
      left: { id: 'left', position: 'left', name: '间夜数', unit: '间', min: 0, max: 100 },
      right: { id: 'right', position: 'right', name: '出租率', unit: '%', min: 0, max: 130 },
    },
    series: [],
  };
  const block = { chartData: empty };
  return {
    last7Days: block,
    last30Days: block,
    last12Months: block,
    next7Days: block,
    next30Days: block,
  };
}

export interface DiscountAnalysisChartBlock {
  categories: string[];
  dataset: {
    discountRate: number[];
    occupancyRate: number[];
    avgListPrice: number[];
    avgPrice: number[];
  };
  axes: Record<string, unknown>;
  series: Array<Record<string, unknown>>;
}

export interface DiscountAnalysisBundleData {
  last7Days: { chartData: DiscountAnalysisChartBlock };
  last30Days: { chartData: DiscountAnalysisChartBlock };
  last12Months: { chartData: DiscountAnalysisChartBlock };
  next7Days: { chartData: DiscountAnalysisChartBlock };
  next30Days: { chartData: DiscountAnalysisChartBlock };
}

export async function getDiscountAnalysisFromProxy(
  hotelId: number | string
): Promise<DiscountAnalysisBundleData> {
  const response = await axios.get<{ code: number; data?: DiscountAnalysisBundleData }>(
    '/api/store-revenue/proxy/discount-analysis',
    { params: { hotelId }, timeout: 60000 }
  );
  if (response.data?.code === 1 && response.data?.data) return response.data.data;
  const empty: DiscountAnalysisChartBlock = {
    categories: [],
    dataset: { discountRate: [], occupancyRate: [], avgListPrice: [], avgPrice: [] },
    axes: {
      left: { id: 'left', position: 'left', name: '出租率', unit: '%', min: 0, max: 130 },
      right: { id: 'right', position: 'right', name: '价格', unit: '元', min: 0, max: 1000 },
      right2: { id: 'right2', position: 'right', name: '折扣率', unit: '%', min: 0, max: 100 },
    },
    series: [],
  };
  const block = { chartData: empty };
  return {
    last7Days: block,
    last30Days: block,
    last12Months: block,
    next7Days: block,
    next30Days: block,
  };
}

export async function getOrderTrafficFromProxy(
  hotelId: number | string
): Promise<OrderTrafficBundleData> {
  const response = await axios.get<{ code: number; data?: OrderTrafficBundleData }>(
    '/api/store-revenue/proxy/order-traffic',
    { params: { hotelId }, timeout: 60000 }
  );
  if (response.data?.code === 1 && response.data?.data) return response.data.data;
  const empty: OrderTrafficChartBlock = {
    categories: [],
    dataset: {
      channel1Orders: [],
      channel2Orders: [],
      channel3Orders: [],
      channel4Orders: [],
      averagePrice: [],
    },
    axes: {
      left: { id: 'left', position: 'left', name: '订单量', unit: '单', max: 100 },
      right: { id: 'right', position: 'right', name: '平均价格', unit: '元', min: 0, max: 1000 },
    },
    series: [],
    comparison: { yearOverYear: 0, weekOverWeek: 0 },
  };
  return { last24Hours: empty, last7Days: empty };
}

export async function getOccupancyAnalysisFromProxy(
  hotelId: number | string
): Promise<OccupancyAnalysisBundleData> {
  const response = await axios.get<{ code: number; data?: OccupancyAnalysisBundleData }>(
    '/api/store-revenue/proxy/occupancy-analysis',
    { params: { hotelId }, timeout: 60000 }
  );
  if (response.data?.code === 1 && response.data?.data) return response.data.data;
  const empty: OccupancyAnalysisChartBlock = {
    chartData: {
      categories: [],
      dataset: { occupancyRate: [], roomOvernightRate: [], hourScale: [], roomNight: [] },
      axes: { left: { id: 'left', position: 'left', name: '出租率', unit: '%', min: 0, max: 150 } },
      series: [],
    },
  };
  return { last7Days: empty, last30Days: empty, last12Months: empty };
}

/** FSM 远期房态单条（日汇总或房型明细，GET /qiuguo-fsm/fsm/hotel/futureRoomStatusList） */
export interface FsmFutureRoomStatusItem {
  date: string;
  week?: string | null;
  allCount: number;
  canSellCount: number;
  banSellCount?: number;
  arriveCount?: number;
  leaveCount?: number;
  aliasId?: number | null;
  aliasName?: string | null;
  futureRoomStatusVoList?: FsmFutureRoomStatusItem[] | null;
}

/** FSM 远期房态列表响应 */
export interface FsmFutureRoomStatusListResponse {
  code: number;
  msg?: string;
  data?: FsmFutureRoomStatusItem[];
}

/**
 * 远期房态：走后端代理（后端统一调 FSM 并写 Redis）
 * GET /api/store-revenue/proxy/future-room-status?hotelId=xxx&startDate=&endDate=
 */
export async function getFutureRoomStatusListFromThirdParty(
  hotelId: number | string,
  startDate?: string,
  endDate?: string
): Promise<FsmFutureRoomStatusItem[]> {
  const params: Record<string, string> = { hotelId: String(hotelId) };
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  const response = await axios.get<{ code: number; data?: FsmFutureRoomStatusItem[] }>(
    '/api/store-revenue/proxy/future-room-status',
    { params, timeout: 20000 }
  );
  if (response.data?.code === 1 && Array.isArray(response.data?.data)) return response.data.data;
  return [];
}

/**
 * 获取门店完整实时数据
 * 后端已做「由树构建的响应」缓存与预热，请求应快速返回，不再做前端限流。
 */
export async function getStoreRevenueData(
  params: StoreRevenueQueryParams
): Promise<StoreRevenueDataResponse['data']> {
  const queryParams: Record<string, number | string> = {
    hotelId: params.hotelId,
  };
  if (params.startDate != null) {
    queryParams.startDate = params.startDate;
  }
  const url = '/api/store-revenue/data';
  try {
    const response = await axios.get<StoreRevenueDataResponse>(url, {
      headers: storeRevenueHeaders(),
      params: queryParams,
    });

    if (response.data.code === 1 && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.message || '获取门店实时数据失败');
  } catch (error: any) {
    if (error.response?.status === 503) {
      throw new Error(error.response?.data?.message ?? '门店营收服务未配置 Token，请联系管理员');
    }
    if (error.response?.status === 401) {
      throw new Error('Token 无效或已过期，请重新登录');
    }
    throw error;
  }
}

/** 进行中请求合并：同一 hotelId 只发一次 store-data-from-cache，避免 Strict Mode 或重复挂载导致重复请求 */
const inFlightStoreDataFromCache = new Map<string, Promise<StoreRevenueDataSlice>>();

/**
 * 仅从后端缓存获取门店数据（订单 + 业务报告），不调第三方；与前端直调的房态、渠道价合并使用。
 * GET /api/store-revenue/store-data-from-cache?hotelId=xxx；同门店进行中只发一次请求。
 */
export async function getStoreDataFromCacheFromBackend(
  hotelId: number | string
): Promise<StoreRevenueDataSlice> {
  const key = String(hotelId);
  const inFlight = inFlightStoreDataFromCache.get(key);
  if (inFlight) return inFlight;

  const promise = (async (): Promise<StoreRevenueDataSlice> => {
    const url = '/api/store-revenue/store-data-from-cache';
    const response = await axios.get<{ code: number; message?: string; data: StoreRevenueDataSlice }>(url, {
      headers: storeRevenueHeaders(),
      params: { hotelId },
    });
    if (response.data.code === 1 && response.data.data) {
      const data = response.data.data;
      const fetchedAt = Date.now();
      try {
        const { setStoreDataFromCacheWithComponentSlices } = await import(
          '../pages/store-revenue-monitor/utils/storeRevenueLocalStorage'
        );
        setStoreDataFromCacheWithComponentSlices(hotelId, data as Record<string, unknown>, fetchedAt);
      } catch {
        // 忽略持久化失败
      }
      return data;
    }
    throw new Error(response.data.message || '获取缓存门店数据失败');
  })().finally(() => {
    inFlightStoreDataFromCache.delete(key);
  });
  inFlightStoreDataFromCache.set(key, promise);
  return promise;
}

/** 按指定日期拉取订单并统计的返回结构（与后端 OrderStatsByDateResult 一致） */
export interface OrderStatsByDateResult {
  date: string;
  hotelId: number;
  orderCount: number;
  validOrderCount: number;
  canceledOrderCount: number;
  totalAmount: number;
  bySource: Record<string, number>;
  byOrderStatus: Record<string, number>;
  byGuestType: Record<string, number>;
  byPaymentStatus: Record<string, number>;
  bySaleType: Record<string, number>;
  byMemberLevel: Record<string, number>;
  roomNightStats: { total: number; average: number; orderCountWithRoomNight: number };
  discountRateStats: { avgDiscountRate: number; countWithDiscountRate: number };
  byDiscountRateBucket: Record<string, number>;
  byDaysInAdvance: Record<string, number>;
  lastOrderTime: number | null;
  lastDayDate: string | null;
  lastDayByHour: Record<string, number>;
}

/**
 * 按指定预抵日期拉取订单并统计
 * GET /api/store-revenue/order-stats-by-date?hotelId=xxx&date=YYYY-MM-DD
 */
export async function getOrderStatsByDate(
  hotelId: number | string,
  date: string
): Promise<OrderStatsByDateResult> {
  const response = await axios.get<{ code: number; message?: string; data?: OrderStatsByDateResult }>(
    '/api/store-revenue/order-stats-by-date',
    { params: { hotelId, date }, headers: storeRevenueHeaders() }
  );
  if (response.data?.code === 1 && response.data?.data) {
    return response.data.data;
  }
  throw new Error(response.data?.message ?? '获取订单统计失败');
}

/** 按在住日期拉取房单列表结果 */
export interface OrderRoomPageListByDateResult {
  total: number;
  list: Array<Record<string, any>>;
  pageNum: number;
  pageSize: number;
  pages?: number;
}

/**
 * 按在住日期范围拉取房单列表（单页，最多 500）
 * GET /api/store-revenue/order-room-page-list-by-date?hotelId=xxx&startDate=YYYY-MM-DD&endDate=YYYY-MM-DD
 */
export async function getOrderRoomPageListByDate(
  hotelId: number | string,
  startDate: string,
  endDate?: string
): Promise<OrderRoomPageListByDateResult> {
  const params: Record<string, string> = { hotelId: String(hotelId), startDate };
  if (endDate) params.endDate = endDate;
  const response = await axios.get<{
    code: number;
    message?: string;
    data?: OrderRoomPageListByDateResult;
  }>('/api/store-revenue/order-room-page-list-by-date', {
    params,
    headers: storeRevenueHeaders(),
  });

  if (response.data?.code === 1 && response.data?.data) {
    return response.data.data;
  }
  throw new Error(response.data?.message ?? '获取房单列表失败');
}

/** FSM 房单列表场景（走后端 Redis 10 分钟） */
export type OrderRoomPageListPreset =
  | 'expected-arrival-today'
  | 'expected-departure-today'
  | 'in-house-today';

export interface OrderRoomPresetApiResult {
  preset: OrderRoomPageListPreset;
  dateLabel: string;
  total: number;
  list: Array<Record<string, unknown>>;
  summary: Record<string, number>;
}

/**
 * GET /api/store-revenue/proxy/order-room-page-list?hotelId=&preset=
 * 后端调 FSM orderRoomPageList，Redis 缓存 10 分钟。
 */
export async function getProxyOrderRoomPageListByPreset(
  hotelId: number | string,
  preset: OrderRoomPageListPreset
): Promise<OrderRoomPresetApiResult> {
  const response = await axios.get<{
    code: number;
    message?: string;
    data?: OrderRoomPresetApiResult;
  }>('/api/store-revenue/proxy/order-room-page-list', {
    params: { hotelId, preset },
    headers: storeRevenueHeaders(),
    timeout: 60000,
  });
  if (response.data?.code === 1 && response.data?.data) {
    return response.data.data;
  }
  throw new Error(response.data?.message ?? '获取房单列表失败');
}

/** 指定日期的平均价（用于房态年同比/周环比） */
export interface AvgPriceByDateResult {
  date: string;
  avgPrice: number;
}

/** 去年今天、七天前的平均价（用于房态辅助线，带日期标明） */
export interface AvgPriceRefs {
  lastYearToday: AvgPriceByDateResult;
  sevenDaysAgo: AvgPriceByDateResult;
}

/**
 * 前端直调 COS「经营分析报表合计」获取指定门店、指定日期的当天平均价
 * GET {COS_BASE_URL}/qiuguo-cos/cos/report/groupBusinessReportTotal?hotelIds=xxx&startDate=YYYY-MM-DD&endDate=YYYY-MM-DD&dateType=0
 * 用于房态年同比/周环比辅助线，结果由调用方做本地缓存（按日更新）。
 */
/**
 * 指定日期平均价：走后端代理（后端统一调 COS 并写 Redis）
 * GET /api/store-revenue/proxy/avg-price?hotelId=xxx&date=YYYY-MM-DD
 * 请求头禁用缓存，避免 304 使用旧空数据导致页面无数据
 */
export async function getAvgPriceByDateFromThirdParty(
  hotelId: number | string,
  date: string
): Promise<AvgPriceByDateResult> {
  const response = await axios.get<{ code: number; data?: { date: string; avgPrice: number } }>(
    '/api/store-revenue/proxy/avg-price',
    { params: { hotelId, date }, timeout: 15000, headers: storeRevenueHeaders() }
  );
  if (response.data?.code === 1 && response.data?.data) {
    return { date: response.data.data.date, avgPrice: response.data.data.avgPrice };
  }
  return { date, avgPrice: 0 };
}

/**
 * 获取指定门店、指定日期的当天平均价（前端直调 COS groupBusinessReportTotal，结果由调用方本地缓存）
 */
export async function getAvgPriceByDate(
  hotelId: number | string,
  date: string
): Promise<AvgPriceByDateResult> {
  try {
    return await getAvgPriceByDateFromThirdParty(hotelId, date);
  } catch {
    return { date, avgPrice: 0 };
  }
}

/**
 * 批量获取多日平均价（用于远期房态年同比/周环比折线）
 * 内部复用 getAvgPriceByDate，按日期顺序返回 avgPrice 数组
 */
export async function getAvgPricesByDates(
  hotelId: number | string,
  dates: string[]
): Promise<number[]> {
  if (!dates.length) return [];
  const results = await Promise.all(dates.map((date) => getAvgPriceByDate(hotelId, date)));
  return results.map((r) => r.avgPrice);
}

/**
 * 前端直调 PMS「按渠道获取房型价格」
 * GET {COS_BASE_URL}/qiuguo-pms/pms/hotelRoomPrice/getRoomPriceByChannel?hotelId=xxx&startDate=yyyy-mm-dd&endDate=yyyy-mm-dd
 * 传参为当天日期 yyyy-mm-dd，用于渠道房型价格组件，结果由调用方做本地缓存。
 */
/**
 * 渠道房型价格：走后端代理（后端统一调 PMS 并写 Redis），返回与直调一致的规范化结构
 * GET /api/store-revenue/proxy/channel-price?hotelId=xxx
 */
export async function getChannelPriceFromThirdParty(
  hotelId: number | string
): Promise<import('../pages/store-revenue-monitor/utils/priceChannelDataTransformer').PriceChannelResponse | null> {
  const response = await axios.get<{ code: number; data?: { code?: number; data?: unknown } }>(
    '/api/store-revenue/proxy/channel-price',
    { params: { hotelId }, timeout: 30000 }
  );
  if (response.data?.code !== 1) return null;
  const raw = response.data?.data;
  const { normalizeChannelPriceApiToResponse } = await import(
    '../pages/store-revenue-monitor/utils/priceChannelDataTransformer'
  );
  return normalizeChannelPriceApiToResponse(
    { code: raw?.code ?? 0, data: raw?.data },
    hotelId
  );
}

/** getAliasPriceQuantity 响应（远期房态用） */
export type AliasPriceQuantityResponse = import('../pages/store-revenue-monitor/utils/priceChannelDataTransformer').AliasPriceQuantityResponse;

/**
 * 前端直调 PMS「获取酒店房型房态最高价、最低价、房量（最多查询 7 天）」
 * GET {COS_BASE_URL}/qiuguo-pms/pms/api/getAliasPriceQuantity?hotelId=xxx&startDate=xxx&endDate=xxx
 * 传参为 Unix 时间戳（秒）。默认查询「未来 7 天（含今天）」：当天 0 点 至 今天+6 天 0 点。
 */
export async function getAliasPriceQuantityFromThirdParty(
  hotelId: number | string,
  startDate?: number,
  endDate?: number
): Promise<AliasPriceQuantityResponse> {
  let start: number;
  let end: number;
  if (endDate != null && startDate != null) {
    start = startDate;
    end = endDate;
  } else {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    start = startDate ?? Math.floor(today.getTime() / 1000);
    const endDay = new Date(today);
    endDay.setDate(endDay.getDate() + 6);
    endDay.setHours(0, 0, 0, 0);
    end = endDate ?? Math.floor(endDay.getTime() / 1000);
  }
  const response = await axios.get<{ code: number; data?: AliasPriceQuantityResponse }>(
    '/api/store-revenue/proxy/room-status-recently',
    { params: { hotelId: String(hotelId), startDate: start, endDate: end }, timeout: 30000 }
  );
  if (response.data?.code === 1 && response.data?.data) return response.data.data;
  return { code: 0, data: [] } as AliasPriceQuantityResponse;
}

/** 按组件请求时后端返回的单块数据（如 { roomStatus: ... }） */
export type StoreRevenueDataSlice = Partial<StoreRevenueDataResponse['data']>;

/**
 * 按组件获取门店数据（单独接口）
 * GET /api/store-revenue/component?hotelId=xxx&componentType=xxx
 * @returns 单块数据，如 { roomStatus: ... } 或 { orderTraffic: ... }
 */
export async function getStoreRevenueComponent(
  hotelId: number | string,
  componentType: string
): Promise<StoreRevenueDataSlice> {
  const url = '/api/store-revenue/component';
  const response = await axios.get<{ code: number; message?: string; data: StoreRevenueDataSlice }>(url, {
    headers: storeRevenueHeaders(),
    params: { hotelId, componentType },
  });
  if (response.data?.code === 1 && response.data?.data != null) {
    return response.data.data;
  }
  throw new Error(response.data?.message ?? '获取门店组件数据失败');
}

/**
 * 按组件获取门店数据（兼容旧用法，走 /data?componentType=xxx）
 * GET /api/store-revenue/data?hotelId=xxx&componentType=room-status
 * @returns 单块数据，如 { roomStatus: ... } 或 { orderTraffic: ... }
 */
export async function getStoreRevenueDataByComponent(
  hotelId: number | string,
  componentType: string
): Promise<StoreRevenueDataSlice> {
  const url = '/api/store-revenue/data';
  const response = await axios.get<{ code: number; message?: string; data: StoreRevenueDataSlice }>(url, {
    headers: storeRevenueHeaders(),
    params: { hotelId, componentType },
  });
  if (response.data?.code === 1 && response.data?.data != null) {
    return response.data.data;
  }
  throw new Error(response.data?.message ?? '获取门店组件数据失败');
}
